// Save storage behind an interface. LocalSaveBackend now; a CloudSaveBackend
// (our own backend, Phase 3) implements the same contract and can wrap the
// local one as an offline cache.
export interface SaveBackend {
  load(): Promise<string | null>;
  save(data: string): Promise<void>;
  clear(): Promise<void>;
}

export class LocalSaveBackend implements SaveBackend {
  constructor(private key = 'tapheroes.save') {}

  async load(): Promise<string | null> {
    try { return localStorage.getItem(this.key); } catch { return null; }
  }

  async save(data: string): Promise<void> {
    try { localStorage.setItem(this.key, data); } catch { /* storage full or blocked */ }
  }

  async clear(): Promise<void> {
    try { localStorage.removeItem(this.key); } catch { /* ignore */ }
  }
}

/** Keeps the previous save as a backup so a corrupt write never loses everything. */
export class BackedUpSave implements SaveBackend {
  private backup: LocalSaveBackend;
  private lastBackupAt = 0;

  constructor(private primary: SaveBackend, backupKey = 'tapheroes.save.backup', private backupEveryMs = 5 * 60_000) {
    this.backup = new LocalSaveBackend(backupKey);
  }

  async load(): Promise<string | null> { return this.primary.load(); }
  async loadBackup(): Promise<string | null> { return this.backup.load(); }

  async save(data: string): Promise<void> {
    // Rotate rarely: a bad write must not overwrite the backup seconds later.
    if (Date.now() - this.lastBackupAt >= this.backupEveryMs) {
      const prev = await this.primary.load();
      if (prev) await this.backup.save(prev);
      this.lastBackupAt = Date.now();
    }
    await this.primary.save(data);
  }

  async clear(): Promise<void> {
    await this.primary.clear();
    await this.backup.clear();
  }
}
